import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  Loader2, Plus, Trash2, Pencil, CheckCircle2, XCircle,
  FlaskConical, KeyRound, CloudUpload,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { apiFetch, clientRequestHeaders, ensureCsrfToken } from "@/lib/sm-api";
import type { AIProviderKey, AIProvider, AIHealthEntry } from "@/lib/ai/types";
import { useAppDialogs } from "@/components/ui/app-dialogs";

export const Route = createFileRoute("/admin/ops/ai/providers")({
  component: AdminAIProvidersPage,
  head: () => ({ meta: [{ title: "Proveedores IA — Admin" }] }),
});

const PROVIDER_LABELS: Record<AIProvider, string> = {
  anthropic: "Anthropic",
  openai: "OpenAI",
  gemini: "Google Gemini",
  "azure-openai": "Azure OpenAI",
  "azure-speech": "Azure Speech",
  openrouter: "OpenRouter",
  groq: "Groq",
  mistral: "Mistral AI",
  deepseek: "DeepSeek",
  ollama: "Ollama (local)",
  mock: "Mock (testing)",
};

const PROVIDERS: AIProvider[] = [
  "anthropic", "openai", "gemini", "azure-openai", "azure-speech",
  "openrouter", "groq", "mistral", "deepseek", "ollama",
];

interface FormState {
  provider: AIProvider;
  label: string;
  apiKey: string;
  region: string;
  endpoint: string;
  priority: string;
  modelOverride: string;
  syncNetlify: boolean;
}

interface VaultAuditRow {
  id: string;
  actor_email: string | null;
  created_at: string;
  provider: string;
  action: string;
  field_name: string | null;
  result: string;
}

const EMPTY_FORM: FormState = {
  provider: "anthropic",
  label: "",
  apiKey: "",
  region: "",
  endpoint: "",
  priority: "0",
  modelOverride: "",
  syncNetlify: false,
};

function defaultModelFromOverrides(overrides: Record<string, string> | null | undefined): string {
  if (!overrides || typeof overrides !== "object") return "";
  return overrides.default ?? Object.values(overrides)[0] ?? "";
}

function AdminAIProvidersPage() {
  const [keys, setKeys] = useState<AIProviderKey[]>([]);
  const { confirm, prompt } = useAppDialogs();
  const [failoverChain, setFailoverChain] = useState<string[]>([]);
  const [preferredProvider, setPreferredProvider] = useState<string | null>(null);
  const [ollamaMeta, setOllamaMeta] = useState<{ endpoint: string; model: string } | null>(null);
  const [audit, setAudit] = useState<VaultAuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const [keysRes, auditRes] = await Promise.all([
        fetch("/api/admin/ai/providers", { credentials: "include" }),
        fetch("/api/admin/ai/providers?audit=1&limit=30", { credentials: "include" }),
      ]);
      const data = await keysRes.json() as {
        keys: AIProviderKey[];
        failoverChain?: string[];
        preferredProvider?: string | null;
        ollama?: { endpoint: string; model: string };
      };
      const auditData = await auditRes.json() as { audit: VaultAuditRow[] };
      setKeys(data.keys ?? []);
      setFailoverChain(data.failoverChain ?? []);
      setPreferredProvider(data.preferredProvider ?? null);
      setOllamaMeta(data.ollama ?? null);
      setAudit(auditData.audit ?? []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  function modelOverridesPayload(): Record<string, string> | undefined {
    const trimmed = form.modelOverride.trim();
    if (!trimmed && !editId) return undefined;
    return trimmed ? { default: trimmed } : {};
  }

  async function handleSave() {
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        provider: form.provider,
        label: form.label,
        syncNetlify: form.syncNetlify,
      };
      if (form.apiKey) body.apiKey = form.apiKey;
      if (form.region) body.region = form.region;
      if (form.endpoint) body.endpoint = form.endpoint;
      if (form.priority) body.priority = Number(form.priority);
      const overrides = modelOverridesPayload();
      if (overrides !== undefined) body.modelOverrides = overrides;

      if (editId) {
        body.id = editId;
        const res = await apiFetch("/api/admin/ai/providers", {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const data = await res.json() as { error?: string; sync?: { ok: boolean; error?: string; envVar?: string } };
        if (!res.ok) {
          toast.error(data.error ?? "No se pudo guardar");
          return;
        }
        if (form.syncNetlify && data.sync) {
          toast[data.sync.ok ? "success" : "error"](
            data.sync.ok
              ? `Sincronizado con Netlify (${data.sync.envVar ?? "env"})`
              : `Sync Netlify falló: ${data.sync.error ?? "error"}`,
          );
        } else {
          toast.success("Cambios guardados");
        }
      } else {
        const keyless = form.provider === "ollama";
        if (!form.apiKey && !keyless) {
          toast.error("API Key requerida");
          return;
        }
        if (form.apiKey) body.apiKey = form.apiKey;
        if (keyless && !form.endpoint) body.endpoint = "http://127.0.0.1:11434";
        if (keyless && !form.modelOverride) {
          body.modelOverrides = { default: "qwen2.5-coder:3b" };
        }
        const res = await apiFetch("/api/admin/ai/providers", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const data = await res.json() as { error?: string; sync?: { ok: boolean; error?: string; envVar?: string } };
        if (!res.ok) {
          toast.error(data.error ?? "No se pudo crear");
          return;
        }
        if (form.syncNetlify && data.sync) {
          toast[data.sync.ok ? "success" : "error"](
            data.sync.ok
              ? `Clave creada y sincronizada (${data.sync.envVar ?? "env"})`
              : `Creada, pero sync Netlify falló: ${data.sync.error ?? "error"}`,
          );
        } else {
          toast.success("Clave agregada");
        }
      }
      setShowForm(false);
      setForm(EMPTY_FORM);
      setEditId(null);
      await load();
    } finally {
      setSaving(false);
    }
  }

  async function handleToggle(key: AIProviderKey) {
    await apiFetch("/api/admin/ai/providers", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: key.id, enabled: !key.enabled }),
    });
    await load();
  }

  async function handleDelete(id: string) {
    if (!(await confirm({ title: "¿Eliminar esta clave?", description: "La acción no se puede deshacer.", destructive: true, confirmLabel: "Eliminar" }))) return;
    await apiFetch(`/api/admin/ai/providers?id=${id}`, {
      method: "DELETE",
    });
    toast.success("Clave eliminada");
    await load();
  }

  async function handleTest(id: string) {
    setBusyId(id);
    try {
      const res = await apiFetch("/api/admin/ai/providers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ op: "test", id }),
      });
      const data = await res.json() as { result?: AIHealthEntry; error?: string };
      const result = data.result;
      if (!result) {
        toast.error(data.error ?? "Test falló");
        return;
      }
      const latency = result.latencyMs == null ? "—" : `${result.latencyMs}ms`;
      if (result.status === "healthy") {
        toast.success(`OK · ${latency}${result.model ? ` · ${result.model}` : ""}`);
      } else {
        toast.error(`${result.status} · ${latency}${result.error ? ` — ${result.error}` : ""}`);
      }
      await load();
    } finally {
      setBusyId(null);
    }
  }

  async function handleRotate(id: string) {
    const apiKey = await prompt({ title: "Nueva API key", inputType: "password", confirmLabel: "Rotar" });
    if (!apiKey?.trim()) return;
    const syncNetlify = await confirm({ title: "¿También sincronizar con Netlify?", confirmLabel: "Sí, sincronizar", cancelLabel: "No" });
    setBusyId(id);
    try {
      const res = await apiFetch("/api/admin/ai/providers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ op: "rotate", id, apiKey: apiKey.trim(), syncNetlify }),
      });
      const data = await res.json() as {
        error?: string;
        sync?: { ok: boolean; error?: string; envVar?: string };
      };
      if (!res.ok) {
        toast.error(data.error ?? "Rotación falló");
        return;
      }
      if (syncNetlify && data.sync) {
        toast[data.sync.ok ? "success" : "error"](
          data.sync.ok
            ? `Rotada y sync Netlify OK (${data.sync.envVar ?? "env"})`
            : `Rotada, sync falló: ${data.sync.error ?? "error"}`,
        );
      } else {
        toast.success("API key rotada");
      }
      await load();
    } finally {
      setBusyId(null);
    }
  }

  async function handleSyncNetlify(id: string) {
    setBusyId(id);
    try {
      const res = await apiFetch("/api/admin/ai/providers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ op: "sync_netlify", id }),
      });
      const data = await res.json() as {
        error?: string;
        sync?: { ok: boolean; error?: string; envVar?: string };
      };
      if (!res.ok || !data.sync) {
        toast.error(data.error ?? "Sync falló");
        return;
      }
      toast[data.sync.ok ? "success" : "error"](
        data.sync.ok
          ? `Sync Netlify OK (${data.sync.envVar ?? "env"})`
          : `Sync falló: ${data.sync.error ?? "error"}`,
      );
      await load();
    } finally {
      setBusyId(null);
    }
  }

  function handleEdit(key: AIProviderKey) {
    setEditId(key.id);
    setForm({
      provider: key.provider,
      label: key.label,
      apiKey: "",
      region: key.region ?? "",
      endpoint: key.endpoint ?? "",
      priority: String(key.priority),
      modelOverride: defaultModelFromOverrides(key.model_overrides),
      syncNetlify: false,
    });
    setShowForm(true);
  }

  function formatAuditWhen(iso: string) {
    return new Date(iso).toLocaleString("es-AR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold">AI Providers — Plataforma</h1>
          <p className="text-sm text-muted-foreground">Claves de API para todos los tenants. Almacenadas cifradas (AES-256).</p>
        </div>
        <Button size="sm" className="gap-2" onClick={() => { setShowForm(true); setForm(EMPTY_FORM); setEditId(null); }}>
          <Plus className="h-4 w-4" />
          Agregar clave
        </Button>
      </div>

      {failoverChain.length > 0 && (
        <div className="space-y-2">
          <div className="rounded-lg border border-amber-200/80 bg-amber-50/80 px-4 py-2.5 text-sm dark:border-amber-900/50 dark:bg-amber-950/30">
            <span className="font-medium text-amber-900 dark:text-amber-200">Failover chain: </span>
            <span className="text-amber-800/90 dark:text-amber-300/90 font-mono text-xs">
              {failoverChain.join(" → ")}
            </span>
            {preferredProvider ? (
              <span className="ml-2 text-xs text-amber-900/80 dark:text-amber-200/80">
                · preferido: <span className="font-mono">{preferredProvider}</span>
              </span>
            ) : null}
          </div>
          {ollamaMeta ? (
            <div className="rounded-lg border border-emerald-200/80 bg-emerald-50/70 px-4 py-2.5 text-sm dark:border-emerald-900/40 dark:bg-emerald-950/30">
              <span className="font-medium text-emerald-900 dark:text-emerald-200">Ollama local: </span>
              <span className="font-mono text-xs text-emerald-800 dark:text-emerald-300">
                {ollamaMeta.endpoint} · modelo {ollamaMeta.model}
              </span>
              <span className="ml-2 text-xs text-muted-foreground">(sin API key)</span>
            </div>
          ) : null}
        </div>
      )}

      {showForm && (
        <div className="rounded-xl border bg-card p-5 space-y-4">
          <h2 className="text-sm font-semibold">{editId ? "Editar clave" : "Nueva clave de proveedor"}</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Proveedor</label>
              <select
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={form.provider}
                onChange={(e) => setForm((f) => {
                  const provider = e.target.value as AIProvider;
                  if (provider === "ollama") {
                    return {
                      ...f,
                      provider,
                      endpoint: f.endpoint || "http://127.0.0.1:11434",
                      modelOverride: f.modelOverride || "qwen2.5-coder:3b",
                      label: f.label || "Ollama local",
                      apiKey: "",
                      syncNetlify: false,
                    };
                  }
                  return { ...f, provider };
                })}
                disabled={!!editId}
              >
                {PROVIDERS.map((p) => (
                  <option key={p} value={p}>{PROVIDER_LABELS[p]}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Etiqueta</label>
              <input
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                placeholder="Ej: Producción Anthropic"
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <label className="text-xs text-muted-foreground">
                API Key {editId && <span className="text-muted-foreground">(dejar vacío para no cambiar)</span>}
              </label>
              <input
                type="password"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm font-mono"
                placeholder={form.provider === "ollama" ? "(opcional — Ollama no usa API key)" : "sk-..."}
                value={form.apiKey}
                onChange={(e) => setForm((f) => ({ ...f, apiKey: e.target.value }))}
                autoComplete="off"
                disabled={form.provider === "ollama"}
              />
              {form.provider === "ollama" ? (
                <p className="text-[11px] text-muted-foreground">
                  Ollama corre en local. Completá Endpoint (ej. http://127.0.0.1:11434) y el modelo override (ej. qwen2.5-coder:3b).
                </p>
              ) : null}
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Región (opcional)</label>
              <input
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                placeholder="ej: eastus"
                value={form.region}
                onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Endpoint (opcional)</label>
              <input
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                placeholder="https://..."
                value={form.endpoint}
                onChange={(e) => setForm((f) => ({ ...f, endpoint: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Prioridad (menor = preferida)</label>
              <input
                type="number"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={form.priority}
                onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Modelo override (opcional)</label>
              <input
                className="w-full rounded-md border bg-background px-3 py-2 text-sm font-mono"
                placeholder="ej: claude-sonnet-4-5"
                value={form.modelOverride}
                onChange={(e) => setForm((f) => ({ ...f, modelOverride: e.target.value }))}
              />
            </div>
            <div className="sm:col-span-2 flex items-center gap-2 pt-1">
              <input
                id="sync-netlify"
                type="checkbox"
                className="h-4 w-4 rounded border"
                checked={form.syncNetlify}
                onChange={(e) => setForm((f) => ({ ...f, syncNetlify: e.target.checked }))}
              />
              <label htmlFor="sync-netlify" className="text-sm">
                Sincronizar con Netlify
              </label>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={saving || !form.label || (!editId && !form.apiKey && form.provider !== "ollama")}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editId ? "Guardar cambios" : "Agregar"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setShowForm(false); setEditId(null); }}>
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex h-32 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-muted-foreground">Proveedor</th>
                <th className="px-4 py-2 text-left font-medium text-muted-foreground">Etiqueta</th>
                <th className="px-4 py-2 text-left font-medium text-muted-foreground">Key hint</th>
                <th className="px-4 py-2 text-left font-medium text-muted-foreground">Modelo</th>
                <th className="px-4 py-2 text-center font-medium text-muted-foreground">Prio.</th>
                <th className="px-4 py-2 text-center font-medium text-muted-foreground">Estado</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => {
                const modelHint = defaultModelFromOverrides(k.model_overrides);
                const busy = busyId === k.id;
                return (
                  <tr key={k.id} className="border-b last:border-0 hover:bg-muted/20">
                    <td className="px-4 py-2 font-medium">{PROVIDER_LABELS[k.provider] ?? k.provider}</td>
                    <td className="px-4 py-2">{k.label}</td>
                    <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                      {k.provider === "ollama" ? "local (sin API key)" : (k.key_hint ?? "—")}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{modelHint || "—"}</td>
                    <td className="px-4 py-2 text-center">{k.priority}</td>
                    <td className="px-4 py-2 text-center">
                      <button onClick={() => handleToggle(k)} title={k.enabled ? "Deshabilitar" : "Habilitar"}>
                        {k.enabled
                          ? <CheckCircle2 className="h-4 w-4 text-green-600 mx-auto" />
                          : <XCircle className="h-4 w-4 text-red-500 mx-auto" />}
                      </button>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1 justify-end flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 gap-1 px-2 text-xs"
                          disabled={busy}
                          onClick={() => handleTest(k.id)}
                          title="Probar"
                        >
                          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <FlaskConical className="h-3 w-3" />}
                          Probar
                        </Button>
                        {k.provider !== "ollama" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 gap-1 px-2 text-xs"
                          disabled={busy}
                          onClick={() => handleRotate(k.id)}
                          title="Rotar"
                        >
                          <KeyRound className="h-3 w-3" />
                          Rotar
                        </Button>
                        ) : null}
                        {k.provider !== "ollama" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 gap-1 px-2 text-xs"
                          disabled={busy}
                          onClick={() => handleSyncNetlify(k.id)}
                          title="Sync Netlify"
                        >
                          <CloudUpload className="h-3 w-3" />
                          Sync Netlify
                        </Button>
                        ) : null}
                        <button className="p-1 hover:text-foreground text-muted-foreground" onClick={() => handleEdit(k)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button className={cn("p-1 text-muted-foreground hover:text-red-600")} onClick={() => handleDelete(k.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {keys.length === 0 && (
                <tr><td colSpan={7} className="py-8 text-center text-sm text-muted-foreground">Sin claves configuradas.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <section className="space-y-2" id="vault-audit">
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="text-sm font-semibold">Vault audit reciente</h2>
          <span className="text-xs text-muted-foreground">Últimos 30 eventos</span>
        </div>
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground">Quién</th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground">Cuándo</th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground">Provider</th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground">Acción</th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground">Campo</th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground">Resultado</th>
              </tr>
            </thead>
            <tbody>
              {audit.map((row) => (
                <tr key={row.id} className="border-b last:border-0">
                  <td className="px-3 py-1.5 text-xs">{row.actor_email ?? "—"}</td>
                  <td className="px-3 py-1.5 text-xs text-muted-foreground whitespace-nowrap">
                    {formatAuditWhen(row.created_at)}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-xs">{row.provider}</td>
                  <td className="px-3 py-1.5 text-xs">{row.action}</td>
                  <td className="px-3 py-1.5 text-xs text-muted-foreground">{row.field_name ?? "—"}</td>
                  <td className="px-3 py-1.5 text-xs">
                    <span className={row.result === "ok" ? "text-green-700" : "text-red-600"}>
                      {row.result}
                    </span>
                  </td>
                </tr>
              ))}
              {audit.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                    Sin eventos de auditoría.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
